/**
 * Wave 6a — Sales Orbit route.
 *
 * `/app/sales-orbit` — 4-tab panel (RevOps · Angebote · Sales-Kollateral ·
 * Prospecting). Compact list + generate form + detail dialog per tab.
 * Talks to `/api/v1/sales-orbit/*`.
 */
import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { api, auth } from "../../lib/api";
import { useT } from "../../lib/i18n";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../../components/ui/tabs";
import { Users, Gift, FileText, Target, Sparkles } from "lucide-react";

const WRITE_ROLES = new Set(["owner", "admin", "manager", "producer"]);
const OFFER_CATS = ["lead_magnet", "trial", "discount", "bundle", "guarantee", "upsell", "free_service"];
const COLLATERAL_KINDS = ["one_pager", "discovery_questions", "objection_handling",
                                          "comparison_sheet", "proposal_template", "followup_script"];
const PROSPECT_PURPOSES = ["recruiting", "partnerships", "enterprise_deals", "sponsors"];

export default function SalesOrbit() {
  const t = useT();
  return (
    <div className="space-y-4" data-testid="sales-orbit-page">
      <header>
        <h1 className="text-2xl font-semibold">{t("sales_orbit.title", "Sales Orbit")}</h1>
        <p className="text-sm text-muted-foreground">
          {t("sales_orbit.subtitle", "RevOps, Angebote, Sales-Kollateral und Prospecting.")}
        </p>
      </header>
      <Tabs defaultValue="revops" className="space-y-4">
        <TabsList data-testid="sales-orbit-tabs">
          <TabsTrigger value="revops" data-testid="tab-revops">
            <Target className="mr-1 size-4" /> RevOps
          </TabsTrigger>
          <TabsTrigger value="offers" data-testid="tab-offers">
            <Gift className="mr-1 size-4" /> {t("sales_orbit.tabs.offers", "Angebote")}
          </TabsTrigger>
          <TabsTrigger value="collateral" data-testid="tab-collateral">
            <FileText className="mr-1 size-4" /> {t("sales_orbit.tabs.collateral", "Sales-Kollateral")}
          </TabsTrigger>
          <TabsTrigger value="prospecting" data-testid="tab-prospecting">
            <Users className="mr-1 size-4" /> Prospecting
          </TabsTrigger>
        </TabsList>
        <TabsContent value="revops"><RevOpsTab /></TabsContent>
        <TabsContent value="offers"><OffersTab /></TabsContent>
        <TabsContent value="collateral"><CollateralTab /></TabsContent>
        <TabsContent value="prospecting"><ProspectingTab /></TabsContent>
      </Tabs>
    </div>
  );
}

// ─── RevOps ─────────────────────────────────────────────────────────

function RevOpsTab() {
  const t = useT();
  const role = (auth.getUser() || {}).role || "";
  const canWrite = WRITE_ROLES.has(role);
  const [items, setItems] = useState([]);
  const [industry, setIndustry] = useState("real_estate");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    const r = await api.get("/sales-orbit/revops");
    setItems(r.data.items || []);
  }, []);
  useEffect(() => { load(); }, [load]);

  async function generate() {
    setBusy(true);
    try {
      await api.post("/sales-orbit/revops/configure", { industry });
      load();
      toast.success(t("sales_orbit.revops.generated", "RevOps-Konfiguration erstellt."));
    } catch (e) { toast.error(t("sales_orbit.revops.gen_failed", "Fehlgeschlagen.")); }
    finally { setBusy(false); }
  }
  async function applyToCrm(rid) {
    try {
      const r = await api.post(`/sales-orbit/revops/${rid}/apply-to-crm-connector`);
      toast.success(`Applied to ${r.data.connectors_updated} connector(s).`);
    } catch (e) { toast.error("Apply fehlgeschlagen."); }
  }

  return (
    <Card data-testid="revops-card">
      <CardHeader className="pb-2"><CardTitle className="text-sm">RevOps-Konfigurationen</CardTitle></CardHeader>
      <CardContent className="space-y-3">
        {canWrite && (
          <div className="flex gap-2">
            <Select value={industry} onValueChange={setIndustry}>
              <SelectTrigger data-testid="revops-industry" className="w-[200px]"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="real_estate">real_estate</SelectItem>
                <SelectItem value="saas">saas</SelectItem>
                <SelectItem value="services">services</SelectItem>
              </SelectContent>
            </Select>
            <Button data-testid="revops-generate-btn" onClick={generate} disabled={busy}>
              <Sparkles className="mr-1 size-4" /> {t("sales_orbit.generate", "Generieren")}
            </Button>
          </div>
        )}
        {items.length === 0 ? (
          <p className="text-sm text-muted-foreground" data-testid="revops-empty">
            {t("sales_orbit.revops.empty", "Noch keine RevOps-Konfigurationen.")}
          </p>
        ) : (
          <ul className="divide-y" data-testid="revops-list">
            {items.map((rc) => (
              <li key={rc.id} className="py-2" data-testid={`revops-row-${rc.id}`}>
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium flex-1">{rc.name}</span>
                  <Badge variant="outline" className="text-[11px]">{rc.industry}</Badge>
                  <Badge className="text-[11px]">{rc.source_attribution_model}</Badge>
                  {canWrite && (
                    <Button size="sm" variant="outline"
                              data-testid={`revops-apply-${rc.id}`}
                              onClick={() => applyToCrm(rc.id)}>
                      Apply to CRM
                    </Button>
                  )}
                </div>
                <div className="text-xs text-muted-foreground mt-1">
                  {(rc.lead_lifecycle_stages || []).map((s) => s.stage || s).join(" → ")}
                </div>
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}

// ─── Offers ─────────────────────────────────────────────────────────

function OffersTab() {
  const t = useT();
  const [items, setItems] = useState([]);
  const [cat, setCat] = useState("lead_magnet");
  const [name, setName] = useState("");
  const load = useCallback(async () => {
    const r = await api.get("/sales-orbit/offers");
    setItems(r.data.items || []);
  }, []);
  useEffect(() => { load(); }, [load]);
  async function generate() {
    if (!name.trim()) return toast.error(t("sales_orbit.offers.name_required", "Name angeben."));
    try {
      await api.post("/sales-orbit/offers/generate", { category: cat, name });
      setName("");
      load();
      toast.success(t("sales_orbit.offers.generated", "Angebot erstellt."));
    } catch (e) { toast.error(t("sales_orbit.offers.gen_failed", "Fehlgeschlagen.")); }
  }
  return (
    <Card data-testid="offers-card">
      <CardHeader className="pb-2"><CardTitle className="text-sm">{t("sales_orbit.tabs.offers", "Angebote")}</CardTitle></CardHeader>
      <CardContent className="space-y-3">
        <div className="grid grid-cols-1 md:grid-cols-[180px_1fr_auto] gap-2">
          <Select value={cat} onValueChange={setCat}>
            <SelectTrigger data-testid="offer-category"><SelectValue /></SelectTrigger>
            <SelectContent>
              {OFFER_CATS.map((c) => (<SelectItem key={c} value={c} data-testid={`offer-cat-${c}`}>{c}</SelectItem>))}
            </SelectContent>
          </Select>
          <Input data-testid="offer-name" placeholder="Name (z.B. Kostenlose Immobilienbewertung)"
                  value={name} onChange={(e) => setName(e.target.value)} />
          <Button data-testid="offer-generate-btn" onClick={generate}>
            {t("sales_orbit.generate", "Generieren")}
          </Button>
        </div>
        {items.length === 0 ? (
          <p className="text-sm text-muted-foreground" data-testid="offers-empty">
            {t("sales_orbit.offers.empty", "Noch keine Angebote.")}
          </p>
        ) : (
          <ul className="divide-y" data-testid="offers-list">
            {items.map((o) => (
              <li key={o.id} className="py-2 space-y-1" data-testid={`offer-row-${o.id}`}>
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium flex-1">{o.name}</span>
                  <Badge variant="outline" className="text-[11px]">{o.category}</Badge>
                  <Badge className="text-[11px]">{o.status}</Badge>
                </div>
                <p className="text-xs text-muted-foreground italic">{o.headline_offer}</p>
                {(o.bonus_stack || []).length > 0 && (
                  <ul className="text-[11px] text-muted-foreground ml-2 list-disc list-inside"
                        data-testid={`offer-bonus-${o.id}`}>
                    {o.bonus_stack.slice(0, 3).map((b, i) => <li key={i}>{b}</li>)}
                  </ul>
                )}
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}

// ─── Collateral ─────────────────────────────────────────────────────

function CollateralTab() {
  const t = useT();
  const [items, setItems] = useState([]);
  const [kind, setKind] = useState("one_pager");
  const [title, setTitle] = useState("");
  const load = useCallback(async () => {
    const r = await api.get("/sales-orbit/collateral");
    setItems(r.data.items || []);
  }, []);
  useEffect(() => { load(); }, [load]);
  async function generate() {
    if (!title.trim()) return toast.error(t("sales_orbit.collateral.title_required", "Titel angeben."));
    try {
      await api.post("/sales-orbit/collateral/generate",
                          { kind, title, audience: "Makler NRW" });
      setTitle("");
      load();
      toast.success(t("sales_orbit.collateral.generated", "Kollateral erstellt."));
    } catch (e) { toast.error(t("sales_orbit.collateral.gen_failed", "Fehlgeschlagen.")); }
  }
  return (
    <Card data-testid="collateral-card">
      <CardHeader className="pb-2"><CardTitle className="text-sm">Sales-Kollateral</CardTitle></CardHeader>
      <CardContent className="space-y-3">
        <div className="grid grid-cols-1 md:grid-cols-[220px_1fr_auto] gap-2">
          <Select value={kind} onValueChange={setKind}>
            <SelectTrigger data-testid="collateral-kind"><SelectValue /></SelectTrigger>
            <SelectContent>
              {COLLATERAL_KINDS.map((k) => (<SelectItem key={k} value={k} data-testid={`collateral-kind-${k}`}>{k}</SelectItem>))}
            </SelectContent>
          </Select>
          <Input data-testid="collateral-title" placeholder="Titel"
                  value={title} onChange={(e) => setTitle(e.target.value)} />
          <Button data-testid="collateral-generate-btn" onClick={generate}>
            {t("sales_orbit.generate", "Generieren")}
          </Button>
        </div>
        {items.length === 0 ? (
          <p className="text-sm text-muted-foreground" data-testid="collateral-empty">
            {t("sales_orbit.collateral.empty", "Noch kein Kollateral.")}
          </p>
        ) : (
          <ul className="divide-y" data-testid="collateral-list">
            {items.map((c) => (
              <li key={c.id} className="py-2" data-testid={`collateral-row-${c.id}`}>
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium flex-1">{c.title}</span>
                  <Badge variant="outline" className="text-[11px]">{c.kind}</Badge>
                  {c.framework && (<Badge className="text-[11px]">{c.framework}</Badge>)}
                </div>
                <pre className="text-[10px] bg-muted p-1 mt-1 rounded font-mono max-h-24 overflow-auto"
                        data-testid={`collateral-preview-${c.id}`}>
                  {(c.content_md || "").slice(0, 320)}
                </pre>
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}

// ─── Prospecting ────────────────────────────────────────────────────

function ProspectingTab() {
  const t = useT();
  const [items, setItems] = useState([]);
  const [purpose, setPurpose] = useState("recruiting");
  const [name, setName] = useState("");
  const load = useCallback(async () => {
    const r = await api.get("/sales-orbit/prospect-lists");
    setItems(r.data.items || []);
  }, []);
  useEffect(() => { load(); }, [load]);
  async function generate() {
    if (!name.trim()) return toast.error(t("sales_orbit.prospecting.name_required", "Name angeben."));
    try {
      await api.post("/sales-orbit/prospect-lists/generate", { name, purpose });
      setName("");
      load();
      toast.success(t("sales_orbit.prospecting.generated", "Liste erstellt."));
    } catch (e) { toast.error(t("sales_orbit.prospecting.gen_failed", "Fehlgeschlagen.")); }
  }
  return (
    <Card data-testid="prospecting-card">
      <CardHeader className="pb-2"><CardTitle className="text-sm">Prospecting-Listen</CardTitle></CardHeader>
      <CardContent className="space-y-3">
        <div className="grid grid-cols-1 md:grid-cols-[220px_1fr_auto] gap-2">
          <Select value={purpose} onValueChange={setPurpose}>
            <SelectTrigger data-testid="prospect-purpose"><SelectValue /></SelectTrigger>
            <SelectContent>
              {PROSPECT_PURPOSES.map((p) => (<SelectItem key={p} value={p} data-testid={`prospect-purpose-${p}`}>{p}</SelectItem>))}
            </SelectContent>
          </Select>
          <Input data-testid="prospect-name" placeholder="Listenname"
                  value={name} onChange={(e) => setName(e.target.value)} />
          <Button data-testid="prospect-generate-btn" onClick={generate}>
            {t("sales_orbit.generate", "Generieren")}
          </Button>
        </div>
        {items.length === 0 ? (
          <p className="text-sm text-muted-foreground" data-testid="prospecting-empty">
            {t("sales_orbit.prospecting.empty", "Noch keine Prospect-Listen.")}
          </p>
        ) : (
          <ul className="divide-y" data-testid="prospecting-list">
            {items.map((p) => (
              <li key={p.id} className="py-2 space-y-1" data-testid={`prospect-row-${p.id}`}>
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium flex-1">{p.name}</span>
                  <Badge variant="outline" className="text-[11px]">{p.purpose}</Badge>
                  <Badge className="text-[11px]">{(p.contact_ids || []).length} contacts</Badge>
                  <Badge className="text-[11px]">{(p.cadence_plan || []).length} steps</Badge>
                </div>
                <div className="text-[11px] text-muted-foreground">
                  {(p.cadence_plan || []).map((s) =>
                    `${s.channel}+${s.delay_days}d`).join(" → ")}
                </div>
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}
